// (c) chaehongee. Confidential and proprietary.

import { useState, useEffect, useRef, type ReactElement, type KeyboardEvent } from 'react'
import styles from './EditableTitle.module.css'

interface EditableTitleProps {
    title: string
    placeholder?: string
    onSave: (newTitle: string) => void
    readOnly?: boolean
}

export function EditableTitle({
    title,
    placeholder = 'Untitled Session',
    onSave,
    readOnly = false,
}: EditableTitleProps): ReactElement {
    const [isEditing, setIsEditing] = useState(false)
    const [value, setValue] = useState(title)
    const inputRef = useRef<HTMLInputElement>(null)

    // Sync local state if external title changes (e.g. loaded from server)
    useEffect(() => {
        setValue(title)
    }, [title])

    // Focus input when editing starts
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [isEditing])

    function handleStartEditing() {
        if (readOnly) return
        setIsEditing(true)
    }

    function handleSave() {
        setIsEditing(false)
        if (value.trim() !== title) {
            onSave(value.trim())
        } else {
            // Revert if empty or unchanged
            setValue(title)
        }
    }

    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            handleSave()
        } else if (e.key === 'Escape') {
            setIsEditing(false)
            setValue(title)
        }
    }

    function handleTitleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
        if (readOnly) return
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleStartEditing()
        }
    }

    if (isEditing) {
        return (
            <input
                ref={inputRef}
                className={styles.input}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                aria-label="Edit session title"
            />
        )
    }

    return (
        <div
            className={`${styles.title} ${!readOnly ? styles.interactive : ''}`}
            onClick={handleStartEditing}
            onKeyDown={!readOnly ? handleTitleKeyDown : undefined}
            role={!readOnly ? 'button' : undefined}
            tabIndex={!readOnly ? 0 : undefined}
            title={!readOnly ? 'Click to rename' : undefined}
        >
            {title || <span className={styles.placeholder}>{placeholder}</span>}
        </div>
    )
}

